import { useState, useMemo } from "react";
import { toast } from "sonner";
import { Pencil, Archive } from "lucide-react";
import { useAgentSessions } from "@/api/queries/sessions";
import { useArchiveSession } from "@/api/mutations/sessions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DateTimeCell } from "@/components/ResourceShell";
import { formatUsd, costClassName } from "@/lib/formatCost";
import { cn } from "@/lib/utils";
import type { Session } from "@/types";
import { RenameSessionDialog } from "./RenameSessionDialog";

const ROW_LIMIT = 50;

interface Props {
  agentId: string;
}

function sessionBadgeVariant(status: string): "session-active" | "session-idle" | "session-archived" {
  if (status === "active") return "session-active";
  if (status === "archived") return "session-archived";
  return "session-idle";
}

export function SessionsSection({ agentId }: Props) {
  const { data, isLoading, isError, error } = useAgentSessions(agentId);
  const [showArchived, setShowArchived] = useState(false);
  const [renameSession, setRenameSession] = useState<Session | null>(null);
  const [archiveSession, setArchiveSession] = useState<Session | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const archiveMutation = useArchiveSession();

  const filtered = useMemo(() => {
    const all = data ?? [];
    return showArchived ? all : all.filter((s) => s.status !== "archived");
  }, [data, showArchived]);

  const totalCost = (data ?? []).reduce((sum, s) => sum + (s.totalCostUsd ?? 0), 0);
  const rows = filtered.slice(0, ROW_LIMIT);

  function handleArchive() {
    if (!archiveSession) return;
    setArchiveError(null);
    archiveMutation.mutate(
      { agentId, sessionId: archiveSession.id },
      {
        onSuccess: () => {
          toast.success("Session archiviert");
          setArchiveSession(null);
        },
        onError: (err) => {
          const msg = (err as Error).message;
          setArchiveError(msg);
          toast.error(`Archive fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <>
      <Card data-testid="sessions-section">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Sessions</CardTitle>
            <div className="text-sm text-subtext0 mt-1">
              Total-Cost:{" "}
              <span className={cn("font-mono", costClassName(totalCost))} data-testid="sessions-total-cost">
                {formatUsd(totalCost)}
              </span>
              {" — "}
              <span data-testid="sessions-count">{data?.length ?? 0} Sessions</span>
            </div>
          </div>
          <label className="text-xs text-subtext0 flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="sessions-show-archived"
            />
            Archived anzeigen
          </label>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && (
            <div className="p-6 space-y-3" data-testid="sessions-loading">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          )}

          {!isLoading && isError && (
            <div className="p-6 text-sm text-red" data-testid="sessions-error">
              Fehler: {(error as Error)?.message ?? "Unbekannt"}
            </div>
          )}

          {!isLoading && !isError && rows.length === 0 && (
            <div className="p-12 text-center text-subtext0" data-testid="sessions-empty">
              {showArchived
                ? "Noch keine Sessions"
                : "Keine aktiven Sessions — Toggle 'Archived anzeigen' falls relevant."}
            </div>
          )}

          {!isLoading && !isError && rows.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Modell</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Turns</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((s) => (
                  <TableRow key={s.id} data-testid={`session-row-${s.id}`}>
                    <TableCell className="font-medium">
                      <div>{s.title}</div>
                      <div className="text-xs text-overlay0 font-mono">{s.id}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={sessionBadgeVariant(s.status)}>{s.status}</Badge>
                    </TableCell>
                    {/* #224 (UC-0): model = konfiguriert, lastObservedModel = beobachtet (kann abweichen). */}
                    <TableCell className="text-xs" data-testid={`session-model-${s.id}`}>
                      <div className="font-mono">{s.model}</div>
                      <div className="text-overlay0">
                        zuletzt beobachtet: {s.lastObservedModel ?? "—"}
                      </div>
                    </TableCell>
                    <TableCell className={cn("text-right font-mono", costClassName(s.totalCostUsd))}>
                      {formatUsd(s.totalCostUsd)}
                    </TableCell>
                    <TableCell className="text-right font-mono">{s.turnCount}</TableCell>
                    <TableCell className="text-xs"><DateTimeCell iso={s.lastUsedAt} /></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setRenameSession(s)}
                          aria-label="Rename"
                          data-testid={`session-rename-${s.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setArchiveSession(s)}
                          disabled={s.status === "archived"}
                          aria-label="Archive"
                          data-testid={`session-archive-${s.id}`}
                        >
                          <Archive className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {filtered.length > ROW_LIMIT && (
            <div
              className="border-t border-border p-3 text-center text-xs text-subtext0"
              data-testid="sessions-overflow-hint"
            >
              Zeige {ROW_LIMIT} von {filtered.length}
            </div>
          )}
        </CardContent>
      </Card>

      {renameSession && (
        <RenameSessionDialog
          open
          onOpenChange={(o) => !o && setRenameSession(null)}
          session={renameSession}
        />
      )}

      <ConfirmDialog
        open={!!archiveSession}
        onOpenChange={(o) => {
          if (!o) {
            setArchiveSession(null);
            setArchiveError(null);
          }
        }}
        title="Session archivieren"
        description={`"${archiveSession?.title ?? ""}" wird archiviert. Daten bleiben in DB, Status ändert sich.`}
        confirmText="Archivieren"
        confirmVariant="destructive"
        isPending={archiveMutation.isPending}
        errorMessage={archiveError ?? undefined}
        onConfirm={handleArchive}
      />
    </>
  );
}
